import { cloneDeep } from 'lodash-es';

const NS = 'TabSelectionService';

export enum EditBookingTabs {
  summary = 'summary',
  details = 'details',
  paymentSummary = 'paymentSummary',
  bookingOptions = 'bookingOptions',
  upsellOptions = 'upsellOptions',
  deliveryAddress = 'deliveryAddress',
  tags = 'tags',
  notes = 'notes',
  links = 'links',
  paymentDetails = 'paymentDetails',
  changes = 'changes',
  bookingHistory = 'bookingHistory',
  venueVisitHistory = 'venueVisitHistory',
  covidGuestList = 'covidGuestList',
  recurringBookings = 'recurringBookings',
  bookedBy = 'bookedBy',
}

// @todo: add new booking tabs as well

interface ITabEntry {
  tabType: EditBookingTabs;
  isVisible: boolean;
  isEnabled: boolean; // should override 'isVisible' is false

  /**
   * This is the title that appears in the list.
   * Having the title here helps enforce order.
   */
  title: string;
}

const editBookingTabsOriginal: ITabEntry[] = [
  { tabType: EditBookingTabs.summary, isVisible: true, isEnabled: true, title: 'Summary' },
  { tabType: EditBookingTabs.changes, isVisible: true, isEnabled: true, title: 'Changes' },
  { tabType: EditBookingTabs.paymentSummary, isVisible: true, isEnabled: true, title: 'Payments' },
  { tabType: EditBookingTabs.paymentDetails, isVisible: true, isEnabled: true, title: 'Payment' },
  { tabType: EditBookingTabs.details, isVisible: true, isEnabled: true, title: 'Details' },
  {
    tabType: EditBookingTabs.bookingOptions,
    isVisible: true,
    isEnabled: true,
    title: 'Booking Options',
  },
  {
    tabType: EditBookingTabs.upsellOptions,
    isVisible: true,
    isEnabled: true,
    title: 'Upsell Options',
  },
  { tabType: EditBookingTabs.tags, isVisible: true, isEnabled: true, title: 'Tags' },
  { tabType: EditBookingTabs.notes, isVisible: true, isEnabled: true, title: 'Notes' },
  { tabType: EditBookingTabs.links, isVisible: true, isEnabled: true, title: 'Links' },
  {
    tabType: EditBookingTabs.bookedBy,
    isVisible: true,
    isEnabled: true,
    title: 'Booked By',
  },
  {
    tabType: EditBookingTabs.bookingHistory,
    isVisible: true,
    isEnabled: true,
    title: 'Booking History',
  },
  {
    tabType: EditBookingTabs.venueVisitHistory,
    isVisible: true,
    isEnabled: true,
    title: 'Venue History',
  },
  {
    tabType: EditBookingTabs.recurringBookings,
    isVisible: true,
    isEnabled: true,
    title: 'Recurring Bookings',
  },
  {
    tabType: EditBookingTabs.covidGuestList,
    isVisible: true,
    isEnabled: true,
    title: 'Guest List',
  },
  {
    tabType: EditBookingTabs.deliveryAddress,
    isVisible: true,
    isEnabled: true,
    title: 'Delivery Address',
  },
];

class TabSelectionService {
  private editBookingTabs: ITabEntry[] = cloneDeep(editBookingTabsOriginal);

  // just used for unit tests
  resetEditBookingTabs(): void {
    this.editBookingTabs = cloneDeep(editBookingTabsOriginal);
  }

  getEditBookingTabsAsObject(): any {
    const obj: any = {};
    this.editBookingTabs.forEach((t) => {
      obj[t.tabType] = t;
      obj[t.tabType].doShow = () => t.isEnabled && t.isVisible;
    });

    return obj;
  }

  updateEditBookingTabEnabled(tabType: EditBookingTabs, isEnabled: boolean): void {
    const existingTab = this.editBookingTabs.find((t) => t.tabType === tabType);
    if (existingTab) {
      existingTab.isEnabled = isEnabled;
    } else {
      console.warn(NS, 'updateEditBookingTabEnabled', 'Unable to find "tabType" of ' + tabType);
    }
  }

  updateEditBookingTabVisibility(tabType: EditBookingTabs, isVisible: boolean): void {
    const existingTab = this.editBookingTabs.find((t) => t.tabType === tabType);
    if (existingTab) {
      existingTab.isVisible = isVisible;
    } else {
      console.warn(NS, 'updateEditBookingTabVisibility', 'Unable to find "tabType" of ' + tabType);
    }
  }

  getEditBookingTabs(): ITabEntry[] {
    return this.editBookingTabs;
  }

  getEditBookingVisibleTabs(): ITabEntry[] {
    return this.editBookingTabs.filter((t) => t.isEnabled && t.isVisible);
  }

  getEditBookingTabByType(tabType: EditBookingTabs): ITabEntry {
    return this.editBookingTabs.find((t) => t.tabType === tabType);
  }

  doShowEditBookingTab(tabType: EditBookingTabs): boolean {
    const tab = this.editBookingTabs.find((t) => t.tabType === tabType);
    if (tab) {
      return tab.isEnabled && tab.isVisible;
    }
    return false;
  }

  /**
   * Gets index of a tab, but filters out hidden ones first
   */
  getEditBookingVisibleTabIndex(tabType: EditBookingTabs): number {
    return this.getEditBookingVisibleTabs().findIndex((t) => t.tabType === tabType);
  }
}

// creates a singleton instance to export
const instance = new TabSelectionService();
export { instance as TabSelectionService };
